/**
 * Iter209 — Buying Signals (Market Scanner).
 *
 * Segment-level global feed powered by OpenAI Web Search. Each signal
 * ships with a source URL, is categorized into 5 lists (expansion /
 * hiring / public_contract / event / growth) and can be added to the
 * CRM Kanban with one click.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, Loader2, RefreshCw, ExternalLink, TrendingUp, Clock,
  Building2, UserPlus, Landmark, CalendarDays, LineChart, Plus, Check,
  Settings, Trash2, GripVertical, EyeOff, Eye, Send, X, MessageCircle,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import ExportButton from "../../components/ExportButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";

// Iter209 — Categories with distinct colors (user asked for color coding).
const CATEGORIES = [
  {
    id: "expansion", label: "Expansão / Nova Localização",
    short: "Expansão", icon: Building2,
    accent: "#10B981", tint: "rgba(16,185,129,0.10)",
    border: "rgba(16,185,129,0.35)",
  },
  {
    id: "hiring", label: "Contratação / Novo Executivo",
    short: "Contratação", icon: UserPlus,
    accent: "#3B82F6", tint: "rgba(59,130,246,0.10)",
    border: "rgba(59,130,246,0.35)",
  },
  {
    id: "public_contract", label: "Licitação / Contrato Público",
    short: "Licitação", icon: Landmark,
    accent: "#F59E0B", tint: "rgba(245,158,11,0.10)",
    border: "rgba(245,158,11,0.35)",
  },
  {
    id: "event", label: "Evento / Conferência",
    short: "Evento", icon: CalendarDays,
    accent: "#A855F7", tint: "rgba(168,85,247,0.10)",
    border: "rgba(168,85,247,0.35)",
  },
  {
    id: "growth", label: "Crescimento / Investimento",
    short: "Crescimento", icon: LineChart,
    accent: "#F97316", tint: "rgba(249,115,22,0.10)",
    border: "rgba(249,115,22,0.35)",
  },
];

const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

export default function SmithAISearchSignals() {
  const [signals, setSignals] = useState([]);
  const [countsByCat, setCountsByCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [activeCat, setActiveCat] = useState("");   // "" = All
  const [lastRun, setLastRun] = useState(null);
  const [addingCrm, setAddingCrm] = useState({});    // {signal_id: true}
  // Iter210 — Kill Filter (hide in-kanban) + Settings modal + Digest.
  const [hideInKanban, setHideInKanban] = useState(true);   // default ON
  const [filteredOut, setFilteredOut] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (activeCat) params.set("category", activeCat);
      if (hideInKanban) params.set("hide_in_kanban", "true");
      const r = await axios.get(
        `${API}/ai-search/market-signals?${params}`);
      setSignals(r.data?.signals || []);
      setCountsByCat(r.data?.counts_by_category || {});
      setFilteredOut(r.data?.filtered_out_in_kanban || 0);
    } catch (ex) {
      toast.error(ex?.response?.data?.error?.message
        || ex?.response?.data?.detail
        || "Falha ao carregar feed");
    } finally { setLoading(false); }
  };

  const loadLastRun = async () => {
    try {
      const r = await axios.get(`${API}/ai-search/market-signals/last-run`);
      setLastRun(r.data);
    } catch (_) { /* silent */ }
  };

  useEffect(() => { load(); }, [activeCat, hideInKanban]);  // eslint-disable-line
  useEffect(() => { loadLastRun(); }, []);

  const sendDigest = async () => {
    if (sendingDigest) return;
    if (!confirm("Enviar Signal Digest agora para mborges@smithprotective.com?")) return;
    setSendingDigest(true);
    try {
      const { data } = await axios.post(
        `${API}/ai-search/market-signals/digest/send`, {});
      if (data.sent) toast.success(`Digest enviado com ${data.signal_count} sinal(is)`);
      else toast.info(data.reason || "Nada fresco para enviar");
    } catch (ex) {
      toast.error(ex?.response?.data?.error?.message
        || ex?.response?.data?.detail
        || "Falha no digest");
    } finally { setSendingDigest(false); }
  };

  const runDiscover = async () => {
    if (scanning) return;
    if (!confirm("Rodar Buying Signals agora? A Claire vai varrer todos os segmentos ativos via Web Search — leva ~1 min por segmento.")) return;
    setScanning(true);
    try {
      const { data } = await axios.post(
        `${API}/ai-search/market-signals/discover`, {},
        { timeout: 15 * 60 * 1000 });   // up to 15min for full sweep
      toast.success(`${data.total_saved} sinal(is) novo(s) descoberto(s) em ${data.segments?.length || 0} segmento(s)`);
      await load(); await loadLastRun();
    } catch (ex) {
      toast.error(ex?.response?.data?.error?.message
        || ex?.response?.data?.detail
        || "Falha ao rodar Find Buying Signals");
    } finally { setScanning(false); }
  };

  const addToCrm = async (sig) => {
    setAddingCrm((s) => ({ ...s, [sig.id]: true }));
    try {
      const { data } = await axios.post(
        `${API}/ai-search/market-signals/${sig.id}/add-to-crm`, {});
      if (data.already) toast.info("Já estava no CRM");
      else toast.success("Prospect criado no Kanban");
      // Update UI locally so the button flips to "no CRM".
      setSignals((prev) => prev.map((x) =>
        x.id === sig.id
          ? { ...x, added_to_crm: true, prospect_id: data.prospect_id }
          : x));
    } catch (ex) {
      toast.error(ex?.response?.data?.error?.message
        || ex?.response?.data?.detail
        || "Falha ao adicionar ao CRM");
    } finally {
      setAddingCrm((s) => { const n = { ...s }; delete n[sig.id]; return n; });
    }
  };

  // Iter306 · Soft-delete a signal (moves to "ignored" history).
  const ignoreSignal = async (sig) => {
    try {
      await axios.post(`${API}/ai-search/market-signals/${sig.id}/ignore`, {});
      setSignals((prev) => prev.filter((x) => x.id !== sig.id));
      toast(`"${(sig.title || "Sinal").slice(0, 40)}…" ignorado`, {
        description: "Fica no histórico como ignorado",
        duration: 5000,
        action: {
          label: "Desfazer",
          onClick: async () => {
            try {
              await axios.post(`${API}/ai-search/market-signals/${sig.id}/unignore`, {});
              setSignals((prev) => [sig, ...prev]);
              toast.success("Sinal restaurado");
            } catch { toast.error("Falha ao restaurar"); }
          },
        },
      });
    } catch { toast.error("Falha ao ignorar"); }
  };

  // Iter306 · Open Claire with the signal preloaded as context.
  const askClaire = (sig) => {
    const msg =
      `Análise deste Buying Signal:\n\n` +
      `**${sig.title}**\n` +
      `Empresa: ${sig.company_name || "—"}\n` +
      `Categoria: ${sig.category || "—"} · Score: ${sig.commercial_relevance_score}/100\n` +
      (sig.location ? `Local: ${sig.location}\n` : "") +
      `Data: ${sig.signal_date || "—"}\n` +
      `Fonte: ${sig.source_url}\n\n` +
      `${sig.summary || ""}\n\n` +
      `Como devo abordar esse lead? Qual pacote de segurança recomendaria?`;
    // Broadcast to ClaireWidget — it listens for `claire:open` events.
    window.dispatchEvent(new CustomEvent("claire:open", {
      detail: { seed_message: msg, signal_id: sig.id },
    }));
  };

  const totalCount = useMemo(
    () => Object.values(countsByCat).reduce((a, b) => a + b, 0),
    [countsByCat]);

  const grouped = useMemo(() => {
    if (activeCat) return { [activeCat]: signals };
    // "All" view: group by category so each list is a clear section.
    const out = {};
    for (const s of signals) {
      const k = s.category || "other";
      (out[k] = out[k] || []).push(s);
    }
    return out;
  }, [signals, activeCat]);

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-ai-signals-header"
          title="Buying Signals"
          subtitle="Gatilhos comerciais AI-descobertos · sempre com fonte"
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#F97316 0%,#7C2D12 100%)", color: "white" }}>
              <Zap className="w-5 h-5" />
            </div>
          }
        />

        <main className="max-w-[1250px] mx-auto px-4 lg:px-6 py-6 space-y-4">
          {/* Iter209 — Last run header + Find Buying Signals CTA */}
          <section data-testid="ai-signals-header-bar"
            className="rounded-2xl border border-white/10 p-3 flex flex-wrap items-center gap-3"
            style={{ background: "rgba(15,23,42,0.6)" }}>
            <div className="text-[11px] text-slate-400 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" />
              {lastRun?.has_run ? (
                <>
                  Última busca: <b className="text-slate-200" data-testid="ai-signals-last-run">
                    {new Date(lastRun.finished_at).toLocaleString("pt-BR")}
                  </b>
                  <span className="text-slate-500">
                    · {lastRun.triggered_by === "cron" ? "cron 08:00 EST" : "manual"}
                    · {lastRun.total_saved || 0} novos
                  </span>
                </>
              ) : (
                <span data-testid="ai-signals-never-run">
                  Nenhuma busca automática ainda. O cron roda todo dia 08:00 America/New_York.
                </span>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              {/* Iter210 — Kill filter toggle */}
              <button type="button"
                onClick={() => setHideInKanban((v) => !v)}
                data-testid="ai-signals-toggle-hide-kanban"
                title={hideInKanban ? `Mostrando apenas empresas novas · ${filteredOut} ocultas` : "Mostrando também empresas já no Kanban"}
                className={`h-9 px-3 rounded-lg text-[11.5px] font-bold inline-flex items-center gap-1.5 border transition-colors ${
                  hideInKanban
                    ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
                    : "border-white/15 text-slate-400 hover:border-white/30"}`}>
                {hideInKanban ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {hideInKanban ? `Ocultando Kanban${filteredOut ? ` (${filteredOut})` : ""}` : "Mostrar tudo"}
              </button>
              <button type="button" onClick={() => setSettingsOpen(true)}
                data-testid="ai-signals-settings-btn"
                title="Configurar segmentos do scan diário"
                className="h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 border border-white/15 hover:border-white/30 inline-flex items-center gap-1.5">
                <Settings className="w-3.5 h-3.5" /> Segmentos
              </button>
              <ExportButton
                source="signals"
                testidPrefix="ai-signals-export"
                filters={{
                  category: activeCat || "",
                }}
              />
              <button type="button" onClick={sendDigest} disabled={sendingDigest}
                data-testid="ai-signals-digest-btn"
                title="Enviar Signal Digest agora"
                className="h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 border border-white/15 hover:border-white/30 inline-flex items-center gap-1.5 disabled:opacity-40">
                {sendingDigest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Digest
              </button>
              <button type="button" onClick={load}
                data-testid="ai-signals-refresh-btn"
                className="h-9 px-3 rounded-lg text-[12px] font-bold text-slate-300 border border-white/15 hover:border-white/30 inline-flex items-center gap-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin":""}`} /> Recarregar
              </button>
              <button type="button" onClick={runDiscover} disabled={scanning}
                data-testid="ai-signals-find-btn"
                className="h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                style={{ background: "linear-gradient(135deg,#F97316 0%,#7C2D12 100%)" }}>
                {scanning
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Buscando via IA…</>
                  : <><Zap className="w-3.5 h-3.5" /> Find Buying Signals</>}
              </button>
            </div>
          </section>

          {/* Iter209 — Category tabs (colored pills) */}
          <section data-testid="ai-signals-category-tabs"
            className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setActiveCat("")}
              data-testid="ai-signals-cat-all"
              className={`h-9 px-3 rounded-lg text-[12px] font-bold border transition-colors
                ${activeCat === ""
                  ? "bg-white/10 border-white/30 text-white"
                  : "border-white/10 text-slate-400 hover:border-white/25"}`}>
              Todos ({totalCount})
            </button>
            {CATEGORIES.map((cat) => {
              const active = activeCat === cat.id;
              const Icon = cat.icon;
              return (
                <button key={cat.id} type="button"
                  onClick={() => setActiveCat(cat.id)}
                  data-testid={`ai-signals-cat-${cat.id}`}
                  className={`h-9 px-3 rounded-lg text-[12px] font-bold border transition-colors inline-flex items-center gap-1.5`}
                  style={{
                    borderColor: active ? cat.accent : cat.border,
                    background: active ? cat.tint : "transparent",
                    color: active ? "white" : cat.accent,
                  }}>
                  <Icon className="w-3.5 h-3.5" />
                  {cat.short} ({countsByCat[cat.id] || 0})
                </button>
              );
            })}
          </section>

          {/* Feed */}
          {loading && (
            <div className="rounded-2xl border border-white/10 p-8 text-center"
                 data-testid="ai-signals-loading">
              <Loader2 className="w-6 h-6 animate-spin text-orange-400 mx-auto" />
            </div>
          )}
          {!loading && signals.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-[12.5px] text-slate-400"
                 data-testid="ai-signals-empty">
              Nenhum sinal ainda. Clique em <b className="text-orange-300">Find Buying Signals</b> para
              disparar uma varredura agora, ou aguarde o cron das 08:00 EST.
            </div>
          )}

          {/* Grouped by category (in "All" view) OR flat list (in filtered view) */}
          {!loading && Object.entries(grouped).map(([catId, list]) => {
            const cat = CAT_MAP[catId];
            if (!cat || list.length === 0) return null;
            const Icon = cat.icon;
            return (
              <section key={catId}
                data-testid={`ai-signals-group-${catId}`}
                className="space-y-2">
                <div className="flex items-center gap-2 pt-1"
                     style={{ color: cat.accent }}>
                  <Icon className="w-4 h-4" />
                  <span className="text-[11px] uppercase tracking-widest font-black">
                    {cat.label}
                  </span>
                  <span className="text-[10px] text-slate-500 font-bold">
                    · {list.length}
                  </span>
                </div>
                <div className="grid gap-2.5 md:grid-cols-2">
                  {list.map((s) => (
                    <SignalCard key={s.id} s={s} cat={cat}
                      adding={!!addingCrm[s.id]}
                      onAdd={() => addToCrm(s)}
                      onIgnore={ignoreSignal}
                      onAskClaire={askClaire} />
                  ))}
                </div>
              </section>
            );
          })}
        </main>
      </div>
      {settingsOpen && (
        <SegmentsModal onClose={() => setSettingsOpen(false)}
                        onSaved={() => { setSettingsOpen(false); toast.success("Segmentos salvos"); }} />
      )}
    </div>
  );
}

function SignalCard({ s, cat, adding, onAdd, onIgnore, onAskClaire }) {
  // Iter306 · Convert 0-100 score into 1-10 visual + high-priority flag.
  const score10 = Math.max(1, Math.min(10, Math.round((s.commercial_relevance_score || 0) / 10)));
  const isHot = (s.commercial_relevance_score || 0) >= 80;
  const relevanceCls = isHot ? "text-emerald-300"
    : (s.commercial_relevance_score || 0) >= 60 ? "text-sky-300"
    : "text-slate-400";
  return (
    <article data-testid={`ai-signals-item-${s.id}`}
      className={`rounded-xl border p-3 transition-colors hover:border-white/25 ${isHot ? "ring-1 ring-rose-400/40" : ""}`}
      style={{ background: cat.tint, borderColor: cat.border }}>
      {isHot && (
        <div className="flex items-center gap-1.5 mb-1.5 -mt-0.5" data-testid={`ai-signals-hot-badge-${s.id}`}>
          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-sm shadow-rose-500/40">
            🔥 Alta Prioridade
          </span>
          <span className="text-[9.5px] text-rose-300 font-bold">score {score10}/10</span>
        </div>
      )}
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap text-[10px]">
            <span className="uppercase tracking-widest font-bold"
                  style={{ color: cat.accent }}>{cat.short}</span>
            <span className={`font-bold ${relevanceCls}`}
                  title={`Score de relevância: ${score10}/10 (${s.commercial_relevance_score}/100)`}>
              <TrendingUp className="w-2.5 h-2.5 inline" /> {score10}/10
            </span>
            {s.signal_date && (
              <span className="text-slate-500">
                <Clock className="w-2.5 h-2.5 inline mr-0.5" /> {s.signal_date}
              </span>
            )}
          </div>
          <h4 className="text-[13.5px] font-black text-white mt-1 leading-tight"
              data-testid={`ai-signals-title-${s.id}`}>
            {s.title}
          </h4>
          <div className="text-[11px] text-slate-300 mt-0.5 font-bold flex items-center gap-1.5 flex-wrap">
            <span>{s.company_name || "—"}</span>
            {s.location && <span className="text-slate-500 font-normal">· {s.location}</span>}
            {s.already_in_kanban && !s.added_to_crm && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-500/25 text-slate-300 border border-slate-400/40 inline-flex items-center gap-0.5"
                    title="Empresa já existe no Kanban"
                    data-testid={`ai-signals-in-kanban-${s.id}`}>
                🎯 Já no Kanban
              </span>
            )}
          </div>
        </div>
      </header>
      {s.summary && (
        <p className="text-[11.5px] text-slate-300 mt-2 line-clamp-3"
           data-testid={`ai-signals-summary-${s.id}`}>
          {s.summary}
        </p>
      )}
      <footer className="flex items-center gap-2 mt-2.5 pt-2 border-t border-white/5 flex-wrap">
        <a href={s.source_url} target="_blank" rel="noreferrer"
           data-testid={`ai-signals-source-${s.id}`}
           className="inline-flex items-center gap-1 text-[10.5px] text-sky-400 hover:text-sky-300 truncate flex-1 min-w-[140px]">
          <ExternalLink className="w-2.5 h-2.5 shrink-0" />
          <span className="truncate">{s.source_title || s.source_url}</span>
        </a>
        {/* Iter306 · Ask Claire — opens ClaireWidget with signal ctx */}
        <button type="button" onClick={() => onAskClaire?.(s)}
                data-testid={`ai-signals-ask-claire-${s.id}`}
                title="Pedir análise à Claire com o contexto deste sinal"
                className="shrink-0 h-7 px-2 rounded-md text-[10.5px] font-bold text-indigo-200 border border-indigo-400/40 hover:bg-indigo-500/15 inline-flex items-center gap-1">
          <MessageCircle className="w-3 h-3" /> Claire
        </button>
        {/* Iter306 · Ignore — soft-delete + history filter */}
        {!s.added_to_crm && (
          <button type="button" onClick={() => onIgnore?.(s)}
                  data-testid={`ai-signals-ignore-${s.id}`}
                  title="Remover da lista (fica no histórico como ignorado)"
                  className="shrink-0 h-7 px-2 rounded-md text-[10.5px] font-bold text-slate-400 border border-white/10 hover:text-slate-100 hover:border-white/25 inline-flex items-center gap-1">
            <X className="w-3 h-3" /> Ignorar
          </button>
        )}
        {s.added_to_crm ? (
          <span className="shrink-0 h-7 px-2.5 rounded-md text-[10.5px] font-bold text-emerald-300 border border-emerald-400/40 inline-flex items-center gap-1"
                data-testid={`ai-signals-added-${s.id}`}>
            <Check className="w-3 h-3" /> No CRM
          </span>
        ) : (
          <button type="button" onClick={onAdd} disabled={adding}
                  data-testid={`ai-signals-add-crm-${s.id}`}
                  className="shrink-0 h-7 px-2.5 rounded-md text-[10.5px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-40"
                  style={{ background: cat.accent }}>
            {adding
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Adicionando…</>
              : <><Plus className="w-3 h-3" /> Adicionar ao CRM</>}
          </button>
        )}
      </footer>
    </article>
  );
}


// Iter210 — Segments Settings modal
function SegmentsModal({ onClose, onSaved }) {
  const [segments, setSegments] = useState([]);
  const [geography, setGeography] = useState("");
  const [defaults, setDefaults] = useState([]);
  const [newSeg, setNewSeg] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/ai-search/market-signals/settings`);
        setSegments(r.data.segments || []);
        setGeography(r.data.geography || "");
        setDefaults(r.data.defaults || []);
      } catch (_) { toast.error("Falha ao carregar settings"); }
      finally { setLoading(false); }
    })();
  }, []);

  const moveUp = (i) => { if (i === 0) return; const c = [...segments]; [c[i-1], c[i]] = [c[i], c[i-1]]; setSegments(c); };
  const moveDown = (i) => { if (i === segments.length - 1) return; const c = [...segments]; [c[i+1], c[i]] = [c[i], c[i+1]]; setSegments(c); };
  const remove = (i) => setSegments(segments.filter((_, idx) => idx !== i));
  const addSeg = () => {
    const s = newSeg.trim();
    if (!s) return;
    if (segments.some((x) => x.toLowerCase() === s.toLowerCase())) {
      toast.error("Segmento já existe"); return;
    }
    setSegments([...segments, s]); setNewSeg("");
  };
  const resetDefaults = () => {
    if (!confirm("Restaurar segmentos default?")) return;
    setSegments(defaults);
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ai-search/market-signals/settings`,
        { segments, geography: geography.trim() });
      onSaved?.();
    } catch (ex) {
      toast.error(ex?.response?.data?.error?.message
        || ex?.response?.data?.detail
        || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
         onClick={() => !saving && onClose()}
         data-testid="ai-signals-settings-modal">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 p-5"
           style={{ background: "#0B1120" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-[16px] font-extrabold text-white">Segmentos do Scan Diário</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Reordene, remova ou adicione segmentos custom. Cron roda 08:00 America/New_York.
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-white/5 text-slate-400 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block text-[10.5px] uppercase tracking-widest text-slate-500 font-bold mb-1">Geografia (opcional)</label>
        <input value={geography} onChange={(e) => setGeography(e.target.value)}
               placeholder="Ex: Florida"
               data-testid="ai-signals-settings-geography"
               className="w-full h-9 px-3 rounded-lg bg-black/40 border border-white/10 text-[12px] text-white focus:border-orange-400 outline-none mb-3" />

        <label className="block text-[10.5px] uppercase tracking-widest text-slate-500 font-bold mb-1">Segmentos ativos</label>
        <div className="rounded-lg border border-white/10 overflow-hidden max-h-64 overflow-y-auto mb-3"
             data-testid="ai-signals-segments-list">
          {loading ? (
            <div className="p-4 text-center text-slate-500 text-[12px]">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Carregando…
            </div>
          ) : segments.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-[12px]">
              Nenhum segmento — adicione um abaixo.
            </div>
          ) : segments.map((s, i) => (
            <div key={i} data-testid={`ai-signals-segment-row-${i}`}
                 className="flex items-center gap-2 px-3 py-2 border-b border-white/5 last:border-b-0 bg-white/[0.02]">
              <GripVertical className="w-3 h-3 text-slate-500 shrink-0" />
              <span className="flex-1 text-[12px] text-white truncate">{s}</span>
              <button onClick={() => moveUp(i)} disabled={i === 0}
                      data-testid={`ai-signals-segment-up-${i}`}
                      className="w-6 h-6 rounded text-[9px] text-slate-400 hover:bg-white/10 disabled:opacity-20">▲</button>
              <button onClick={() => moveDown(i)} disabled={i === segments.length - 1}
                      data-testid={`ai-signals-segment-down-${i}`}
                      className="w-6 h-6 rounded text-[9px] text-slate-400 hover:bg-white/10 disabled:opacity-20">▼</button>
              <button onClick={() => remove(i)}
                      data-testid={`ai-signals-segment-remove-${i}`}
                      className="w-6 h-6 rounded text-red-400 hover:bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <input value={newSeg} onChange={(e) => setNewSeg(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && addSeg()}
                 placeholder="Ex: REIT commercial real estate"
                 data-testid="ai-signals-segments-new-input"
                 className="flex-1 h-9 px-3 rounded-lg bg-black/40 border border-white/10 text-[12px] text-white focus:border-orange-400 outline-none" />
          <button onClick={addSeg}
                  data-testid="ai-signals-segments-add-btn"
                  className="h-9 px-3 rounded-lg text-[12px] font-bold text-white bg-white/10 hover:bg-white/15 inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={resetDefaults}
                  data-testid="ai-signals-segments-reset-btn"
                  className="h-9 px-3 rounded-lg text-[11.5px] font-bold text-slate-400 hover:bg-white/5">
            Restaurar padrão
          </button>
          <button onClick={onClose} disabled={saving}
                  className="ml-auto h-9 px-4 rounded-lg text-[12px] font-bold text-slate-300 hover:bg-white/5">
            Cancelar
          </button>
          <button onClick={save} disabled={saving}
                  data-testid="ai-signals-segments-save-btn"
                  className="h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg,#F97316 0%,#7C2D12 100%)" }}>
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Salvando…</> : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

